import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight } from "lucide-react";

// Slide-in navigation drawer for < lg viewports.
export const MobileMenu = ({ open, onClose, nav, pdpLinks, hpLinks = [], currentPath }) => {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            className="fixed inset-0 z-[60] bg-neutral-900/40 lg:hidden"
            data-testid="mobile-menu-scrim"
          />
          <motion.aside
            key="drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-y-0 right-0 z-[70] w-[86vw] max-w-[400px] bg-white flex flex-col lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-[60px] border-b border-neutral-200">
              <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500">
                Menu
              </span>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
                className="w-10 h-10 -mr-2 flex items-center justify-center text-neutral-800"
              >
                <X size={20} strokeWidth={1.6} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-6 py-6">
              {nav.map((item, i) => (
                <motion.a
                  key={item.title}
                  href={item.link}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.12 + i * 0.05, duration: 0.4 }}
                  data-testid={`mobile-nav-${item.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                  className="group flex items-center justify-between py-4 border-b border-neutral-100 text-[26px] leading-none tracking-[-0.01em] text-neutral-900"
                >
                  {item.title}
                  <ArrowRight size={18} strokeWidth={1.6} className="text-neutral-300 group-hover:text-riversol-blue transition-colors" />
                </motion.a>
              ))}
              <div className="mt-8 font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500">
                Homepages
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {hpLinks.map((p) => (
                  <a
                    key={p.label}
                    href={p.href || "#"}
                    data-testid={`mobile-hps-item-${p.label.toLowerCase()}`}
                    className={`rounded-full border px-4 py-2 text-[12px] tracking-[0.06em] transition-colors ${
                      p.href && currentPath === p.href
                        ? "bg-riversol-blue text-white border-riversol-blue"
                        : "border-neutral-300 text-neutral-700"
                    }`}
                  >
                    {p.label}
                  </a>
                ))}
              </div>
              <div className="mt-8 font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500">
                Prototype PDPs
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {pdpLinks.map((p) => (
                  <a
                    key={p.href}
                    href={p.href}
                    data-testid={`mobile-pdps-item-${p.label.toLowerCase().replace(/\./g, "-")}`}
                    className={`rounded-full border px-4 py-2 text-[12px] tracking-[0.06em] transition-colors ${
                      currentPath === p.href
                        ? "bg-riversol-blue text-white border-riversol-blue"
                        : "border-neutral-300 text-neutral-700"
                    }`}
                  >
                    {p.label}
                  </a>
                ))}
              </div>
            </nav>
            <div className="px-6 py-5 border-t border-neutral-200">
              <a
                href="https://www.riversol.com/pages/skin-analyzer"
                data-testid="mobile-menu-analyzer-cta"
                className="flex items-center justify-center gap-2 w-full bg-riversol-blue text-white rounded-full px-6 py-4 text-[13px] tracking-[0.14em] uppercase font-medium"
              >
                Take the Skin Analyzer
                <ArrowRight size={16} strokeWidth={1.8} />
              </a>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileMenu;
